import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { selectCustomerData, selectCustomerContactData } from '../../modules/customer/selectors';
import { selectErrors } from '../../modules/global/selectors';
import { fetchCustomerData } from '../../modules/customer/actions';
import { Error } from '../../components/Error';
import {getUserId, setupSessionStorage} from '../../utils/helpers';
import { PasswordForm } from '../../components/PasswordForm';
import { OfferingType } from '../../utils/offeringType';
import { ApiRequest } from '../../utils/api';
import { useApiMutation } from '../../utils/apiHooks';
import _ from 'lodash';

interface Props {
	errors: any[];
	customer: any;
	doFetchCustomerData: () => void;
	customerContactData: any;
	offeringType: OfferingType;
}

const Heading = styled.h1`
	text-align: center;
	font-size: 1.8rem;
	line-height: 1.3;
	white-space: nowrap;

	@media print, screen and (min-width: 500px) {
		font-size: 2.25rem;
	}
`;

const Subhead = styled.h4`
	font-size: 1.8rem;
	margin-bottom: 1px;
	text-align: center;
	margin-top: 3.5rem;
`;

export const UpdatePasswordPageImpl: React.FC<Props> = (props: Props) => {
	const [password, setPassword] = useState<string | undefined>(undefined);

	const [updatePassword, { isSuccess: updatePasswordSuccess }] = useApiMutation((data: { password: string; oldPassword: string }) =>
		ApiRequest.updatePassword(getUserId()!, data.password, data.oldPassword)
	);

	const [loginUser, { error: loginUserError, data: loginUserData }] = useApiMutation((email) => ApiRequest.login(email, password));

	useEffect(() => {
		if (!props.customer) {
			props.doFetchCustomerData();
		}
	}, []); // eslint-disable-line

	useEffect(() => {
		if (loginUserData) {
			setupSessionStorage(loginUserData.accessToken, getUserId()!)
		}
		if (loginUserError) {
			ApiRequest.logoutUnauthorized();
		}
	}, [loginUserData, loginUserError]);

	useEffect(() => {
		if (updatePasswordSuccess) {
			_.delay(() => {
				loginUser(getUserId());
			}, 2000);
		}
	}, [updatePasswordSuccess]); // eslint-disable-line

	const doPasswordCheck = (password: string, oldPassword: string) => {
		setPassword(password);
		updatePassword({
			password,
			oldPassword
		});
	};

	const { errors } = props;
	const noErrors = errors && errors.length < 1;
	const PASSWORD_MIN_LENGTH = 12;

	return (
		<div>
			<Helmet title="Päivitä salasanasi" meta={[{ name: 'description', content: 'Päivitä salasanasi' }]} />
			{noErrors && !updatePasswordSuccess && (
				<React.Fragment>
					<Heading>Päivitä salasanasi</Heading>
					<PasswordForm onUpdateSubmit={doPasswordCheck} />
				</React.Fragment>
			)}
			{noErrors && updatePasswordSuccess && (
				<div>
					<Subhead>Salasana päivitetty onnistuneesti</Subhead>
				</div>
			)}
			{!noErrors && <Error message={'Salasanan päivitys epäonnistui. Tarkista salasanasi'} />}
		</div>
	);
};

const mapStateToProps = (state: any) => ({
	errors: selectErrors(state),
	customer: selectCustomerData(state),
	customerContactData: selectCustomerContactData(state)
});

const mapDispatchToProps = (dispatch: any) => ({
	doFetchCustomerData: () => dispatch(fetchCustomerData())
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdatePasswordPageImpl);
